import { nanoid } from "nanoid";
import { useEffect } from "react";
import equifax from "../../assets/equifax.svg";
import { InputField } from "../Common/inputField";
import { DatePickerInput } from "../Common/datePicker";
import { PhoneNumberInputField } from "../Common/phoneNumberInput";
import { useAddressLookupStore } from "../../stores/addressLookupStore";
import { useUserMortgageProfileStore } from "../../stores/userProfileStore";
import { useConsentApplicantsStore } from "../../stores/creditConsentStore";
import { AddressLookupInput } from "../Common/AddressLookup/addressLookpInput";

const CreditConsentView = () => {
  const { userMortgageProfile } = useUserMortgageProfileStore();
  const { formattedAddresses, setSelectedAddress } = useAddressLookupStore();

  const {
    consentApplicants,
    addConsentApplicants,
    updateConsentApplicantsField,
    clearConsentApplicantsStore,
  } = useConsentApplicantsStore();

  useEffect(() => {
    if (consentApplicants.length > 0) {
      return;
    }
    const existingApplicants = [...consentApplicants];

    clearConsentApplicantsStore();

    if (userMortgageProfile) {
      const id = nanoid();
      const isProfileComplete = userMortgageProfile.status !== "INCOMPLETE"; 
      addConsentApplicants(id);
      
      updateConsentApplicantsField(
        id,
        "firstName",
        userMortgageProfile.firstName || ""
      );
      updateConsentApplicantsField(
        id,
        "middleName",
        userMortgageProfile.middleName || ""
      );
      updateConsentApplicantsField(
        id,
        "lastName",
        userMortgageProfile.lastName || ""
      );
      updateConsentApplicantsField(
        id,
        "address",
        userMortgageProfile.address?.address1 || ""
      );
      updateConsentApplicantsField(
        id,
        "address2",
        userMortgageProfile.address?.address2 || ""
      );
      updateConsentApplicantsField(
        id,
        "city",
        userMortgageProfile.address?.city || ""
      );
      updateConsentApplicantsField(
        id,
        "postalCode",
        userMortgageProfile.address?.postalCode || ""
      );
      updateConsentApplicantsField(
        id,
        "province",
        userMortgageProfile.address?.state || ""
      );
      updateConsentApplicantsField(
        id,
        "phone",
        userMortgageProfile.phoneNumber || ""
      );
      updateConsentApplicantsField(
        id,
        "dateOfBirth",
        userMortgageProfile.dateOfBirth || null
      );
      const existingConsent = consentApplicants.find(a => a.id === id)?.consent;
      updateConsentApplicantsField(
        id,
        "consent",
        existingConsent !== undefined ? existingConsent : isProfileComplete
      );
  
      if (userMortgageProfile.address) {
        const { address1, address2, city, state, postalCode } = userMortgageProfile.address;
        const fullAddress = [
          address1,
          address2,
          city,
          state,
          postalCode
        ].filter(Boolean).join(", ");
        setSelectedAddress(id, fullAddress);
      }

      if (userMortgageProfile.coApplicants) {
        for (const coApplicant of userMortgageProfile.coApplicants) {
          const id = nanoid();
          addConsentApplicants(id);
          updateConsentApplicantsField(id, "firstName", coApplicant.firstName);
          updateConsentApplicantsField(
            id,
            "middleName",
            coApplicant.middleName || ""
          );
          updateConsentApplicantsField(id, "lastName", coApplicant.lastName);
          updateConsentApplicantsField(
            id,
            "address",
            coApplicant.address?.address1 || ""
          );
          updateConsentApplicantsField(
            id,
            "address2",
            coApplicant.address?.address2 || ""
          );
          updateConsentApplicantsField(
            id,
            "city",
            coApplicant.address?.city || ""
          );
          updateConsentApplicantsField(
            id,
            "postalCode",
            coApplicant.address?.postalCode || ""
          );
          updateConsentApplicantsField(
            id,
            "province",
            coApplicant.address?.state || ""
          );
          updateConsentApplicantsField(id, "phone", coApplicant.phoneNumber);
          updateConsentApplicantsField(
            id,
            "dateOfBirth",
            coApplicant.dateOfBirth || null
          );
          const existingCoConsent = consentApplicants.find(a => a.id === id)?.consent;
          updateConsentApplicantsField(
            id,
            "consent",
            existingCoConsent !== undefined ? existingCoConsent : isProfileComplete
          );
          if (coApplicant.address) {
            const { address1, address2, city, state, postalCode } = coApplicant.address;
            const fullAddress = [
              address1,
              address2,
              city,
              state,
              postalCode
            ].filter(Boolean).join(", ");

            setSelectedAddress(id, fullAddress);
          }
        }
      }
    }

    for (const id of Object.keys(formattedAddresses)) {
      const formattedAddress = formattedAddresses[id];
      if (formattedAddress) {
        updateConsentApplicantsField(id, "address", formattedAddress.address1);
        updateConsentApplicantsField(
          id,
          "address2",
          formattedAddress.address2 || ""
        );
        updateConsentApplicantsField(id, "city", formattedAddress.city);
        updateConsentApplicantsField(
          id,
          "postalCode",
          formattedAddress.postalCode
        );
        updateConsentApplicantsField(id, "province", formattedAddress.state);
      }
    }
  }, [
    userMortgageProfile,
    addConsentApplicants,
    clearConsentApplicantsStore,
    updateConsentApplicantsField,
    formattedAddresses,
    setSelectedAddress,
    consentApplicants,
  ]);

  

  return (
    <div className="space-y-10">
      {consentApplicants.map((applicant) => (
        <div className="space-y-4" key={applicant.id}>
          <div className="px-3">
            <div className="font-bold text-2xl">
              {applicant.firstName || "Applicant"}'s Credit Consent{" "}
            </div>
            <div className="text-[#6B7280] mt-1">
              This is a soft credit check and will not effect your credit score.
            </div>
          </div>
          <hr className="bg-[#6B7280]" />
          <div className="rounded-[8px] border-[#E5E7EB] border-2 mb-4">
            <div className="font-medium flex items-center justify-between w-full bg-[#E5E7EB]/50 rounded-t-[8px] border-b-[#E5E7EB] border-b-2 p-3">
              <div>Soft Credit Check for {applicant.firstName || "Applicant"}</div>
              <img src={equifax} alt="Equifax Logo" className="flex" />
            </div>
            <div className="p-4 space-y-6">
              <div className="space-y-2">
                <div className="grid lg:grid-cols-3 gap-4">
                  <div className="space-y-1 w-full">
                    <InputField
                      id={`firstName-${applicant.id}`}
                      label="First Name*"
                      value={applicant.firstName}
                      onChange={(e) =>
                        updateConsentApplicantsField(
                          applicant.id,
                          "firstName",
                          e.target.value
                        )
                      }
                    />
                    {applicant.errors?.firstName && (
                      <p className="text-red-600 text-sm">
                        {applicant.errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 w-full">
                    <InputField
                      id={`middleName-${applicant.id}`}
                      label="Middle Name"
                      value={applicant.middleName}
                      onChange={(e) =>
                        updateConsentApplicantsField(
                          applicant.id,
                          "middleName",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="space-y-1 w-full">
                    <InputField
                      id={`lastName-${applicant.id}`}
                      label="Last Name*"
                      value={applicant.lastName}
                      onChange={(e) =>
                        updateConsentApplicantsField(
                          applicant.id,
                          "lastName",
                          e.target.value
                        )
                      }
                    />
                    {applicant.errors?.lastName && (
                      <p className="text-red-600 text-sm">
                        {applicant.errors.lastName}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid lg:grid-cols-3 gap-4">
                  <div className="space-y-1 w-full">
                    <DatePickerInput
                      label="Date of Birth*"
                      selectedDate={applicant.dateOfBirth}
                      onDateChange={(date) =>
                        updateConsentApplicantsField(
                          applicant.id,
                          "dateOfBirth",
                          date
                        )
                      }
                      disableFuture={true}
                    />
                    {applicant.errors?.dateOfBirth && (
                      <p className="text-red-600 text-sm">
                        {applicant.errors.dateOfBirth}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 w-full">
                    <PhoneNumberInputField
                      id={`phone-${applicant.id}`}
                      label="Phone Number*"
                      value={applicant.phone}
                      onChange={(value) =>
                        updateConsentApplicantsField(
                          applicant.id,
                          "phone",
                          value
                        )
                      }
                    />
                    {applicant.errors?.phone && (
                      <p className="text-red-600 text-sm">
                        {applicant.errors.phone}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 w-full">
                    <AddressLookupInput type={applicant.id} />
                  </div>
                </div>
                <div className="flex items-start pt-4">
                  <input
                    type="checkbox"
                    checked={applicant.consent}
                    onChange={(e) =>
                      updateConsentApplicantsField(
                        applicant.id,
                        "consent",
                        e.target.checked
                      )
                    }
                    id={`consent-checkbox-${applicant.id}`}
                    className="w-4 h-4 text-z-dark-moss-green bg-gray-100 border-gray-300 rounded focus:ring-z-dark-moss-green focus:ring-2"
                  />
                  <label
                    htmlFor={`consent-checkbox-${applicant.id}`}
                    className="ml-2 text-xs font-medium text-[#6B7280]"
                  >
                    I confirm that the information above is my own and I
                    understand and authorize Zown to securely access my credit
                    report.
                  </label>
                </div>
                {applicant.errors?.consent && (
                  <p className="text-red-600 text-sm">
                    {applicant.errors.consent}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export { CreditConsentView };
